import { Injectable } from '@angular/core';
import { Resource } from './resource.model';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveDataTypes } from '@klickdata/core/mobile';
import { filter, switchMap, tap, of, Observable } from 'rxjs';
import { ResourceService } from './resource.service';

@Injectable({
  providedIn: 'root',
})
export class ResourcePublicationService {
  constructor(
    private readonly mobile: MobileService,
    private readonly resourceService: ResourceService,
    private readonly message: MessageService
  ) {}

  makeResourcePublic(resource: Resource): Observable<Resource> {
    if (resource.publication) {
      const publicationData = [
        {
          type: 'approved',
          icon: 'check_circle',
          iconClass: 'blue',
          titleClass: 'blue',
          title: $localize`Publication request approved`,
          contentBody: $localize`Well done, the administrator has approved you request to publication`,
        },
        {
          type: 'rejected',
          icon: 'cancel',
          iconClass: 'red',
          titleClass: 'red',
          title: $localize`Publication request rejected`,
          contentBody: $localize`Unfortunately the administrator has rejected you request to publication`,
        },
        {
          type: 'pending',
          icon: 'pending_actions',
          iconClass: 'orange',
          titleClass: 'orange',
          title: $localize`Publication request pending`,
          contentBody: $localize`The administrator is still reviewing you request to publication`,
        },
      ];

      const currentPub = publicationData.find((pub) => pub.type == resource?.publication?.approval_status);

      const msgBody = resource.publication.admin?.message?.body;
      this.mobile.updateSideNavSub({
        dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
        data: {
          icon: currentPub.icon,
          iconClass: currentPub.iconClass,
          titleClass: currentPub.titleClass,
          title: currentPub.title,
          contentBody: currentPub.contentBody,
          secContentBody: msgBody
            ? '<span>' + $localize`The administrator message: ` + `</span><span>${msgBody}</span>`
            : null,
          secContentClass: msgBody ? 'reviewer-msg' : null,
          positiveBtn: $localize`Ok`,
          negativeBtn: $localize`Cancel`,
        },
      });
      return of(resource);
    } else {
      const actions = [
        { label: $localize`Request publication`, value: 1, class: 'pos-btn' },
        { label: $localize`Cancel`, value: 0, class: 'null-btn' },
      ];

      this.mobile.updateSideNavSub({
        dataType: SideNaveDataTypes.RESOURCE_ENROLLMENT_REQUEST,
        data: {
          icon: 'public',
          title: $localize`Request to make public`,
          inputDesc: $localize`Send message to admin requesting to publication`,
          resource: resource,
          actions: actions,
          type: 'publicationRequest',
        },
      });
      return this.mobile.getSideNavResponseData().pipe(
        filter((data) => data.type === 'publicationRequest' && data.response),
        switchMap((data) =>
          this.resourceService.userRequestPermission(
            resource.id,
            {
              message: { body: data.value },
              eager: ['publication'],
            },
            'publication_requested'
          )
        ),
        tap(() => this.message.openMessage(MessageSavedComponent, $localize`Request sent to the admin.`))
      );
    }
  }

  publishResource(resource: Resource): Observable<Resource> {
    if (resource.publishment) {
      const publishmentData = [
        {
          type: 'approved',
          icon: 'check_circle',
          iconClass: 'blue',
          titleClass: 'blue',
          title: $localize`Publish request approved`,
          contentBody: $localize`Well done, the administrator has approved you request to publish`,
        },
        {
          type: 'rejected',
          icon: 'cancel',
          iconClass: 'red',
          titleClass: 'red',
          title: $localize`Publish request rejected`,
          contentBody: $localize`Unfortunately the administrator has rejected you request to publish`,
        },
        {
          type: 'pending',
          icon: 'pending_actions',
          iconClass: 'orange',
          titleClass: 'orange',
          title: $localize`Publish request pending`,
          contentBody: $localize`The administrator is still reviewing you request to publish`,
        },
      ];

      const currentPub = publishmentData.find((pub) => pub.type == resource?.publishment?.approval_status);

      const msgBody = resource.publishment.admin?.message?.body;
      this.mobile.updateSideNavSub({
        dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
        data: {
          icon: currentPub.icon,
          iconClass: currentPub.iconClass,
          titleClass: currentPub.titleClass,
          title: currentPub.title,
          contentBody: currentPub.contentBody,
          secContentBody: msgBody
            ? '<span>' + $localize`The administrator message: ` + `</span><span>${msgBody}</span>`
            : null,
          secContentClass: msgBody ? 'reviewer-msg' : null,
          positiveBtn: $localize`Ok`,
          negativeBtn: $localize`Cancel`,
        },
      });
      return of(resource);
    } else {
      const actions = [
        { label: $localize`Request publishment`, value: 1, class: 'pos-btn' },
        { label: $localize`Cancel`, value: 0, class: 'null-btn' },
      ];

      this.mobile.updateSideNavSub({
        dataType: SideNaveDataTypes.RESOURCE_ENROLLMENT_REQUEST,
        data: {
          icon: 'published_with_changes',
          title: $localize`Request to publish`,
          inputDesc: $localize`Send message to admin requesting to publish`,
          resource: resource,
          actions: actions,
          type: 'publishmentRequest',
        },
      });
      return this.mobile.getSideNavResponseData().pipe(
        filter((data) => data.type === 'publishmentRequest' && data.response),
        switchMap((data) =>
          this.resourceService.userRequestPermission(
            resource.id,
            {
              message: { body: data.value },
              eager: ['publishment'],
            },
            'publish_requested'
          )
        ),
        tap(() => this.message.openMessage(MessageSavedComponent, $localize`Request sent to the admin.`))
      );
    }
  }
}
